import React, { type ReactNode } from 'react'
import { useI18n } from '../../i18n'
import AssetsInventoryTable from './AssetsInventoryTable'
import { componentScope } from '../../lib/cssScope'
import { Select } from '@/components/ui/Select'
import { SearchField } from '@/components/ui/SearchField'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import filterStyles from '@/styles/components/assets/AssetsInventoryFilters.module.scss'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { featureFlags } from '../../config/featureFlags'
import {
  ASSET_CATEGORY_VIEWS,
  assetCategoryI18nKey,
  filterTypesForCategoryView,
  type AssetCategoryView,
} from '../../constants/assetTaxonomy'

interface Asset {
  id: string
  assetId: string
  name: string
  type: string
  environment: string
  ipAddress: string
  standards: string[]
  complianceScore: number
  riskLevel: string
  lastAssessment: string
  vulnerabilities: {
    critical: number
    high: number
    medium: number
    low: number
    total: number
  }
  criticality?: string
  riskScore?: number
  createdAt?: string
  owner?: {
    id: string
    name: string
    department?: string
    email?: string
  }
  lastScan?: string
  posture?: {
    os?: string
    patchLevel?: number
    edrInstalled: boolean
    firewallEnabled: boolean
    encryptionEnabled: boolean
  }
}

interface AssetsInventoryCardProps {
  assets: Asset[]
  visibleAssets: Asset[]
  sortBy: 'critical' | 'high' | 'medium' | 'total' | 'name'
  onSortChange: (sort: 'critical' | 'high' | 'medium' | 'total' | 'name') => void
  severityFilter: Set<'critical' | 'high' | 'medium' | 'low'>
  onSeverityFilterChange: (severity: 'critical' | 'high' | 'medium' | 'low', checked: boolean) => void
  typeFilter: Set<string>
  onTypeFilterChange: (type: string, checked: boolean) => void
  criticalityFilter: Set<string>
  onCriticalityFilterChange: (criticality: string, checked: boolean) => void
  ownerFilter: Set<string>
  onOwnerFilterChange: (ownerId: string, checked: boolean) => void
  environmentFilter: Set<string>
  onEnvironmentFilterChange: (environment: string, checked: boolean) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  availableTypes: string[]
  availableCriticalities: string[]
  availableOwners: Array<{ id: string; name: string }>
  availableEnvironments: string[]
  selectedIds: Set<string>
  onSelectAsset: (assetId: string, selected: boolean) => void
  onSelectAll: (selected: boolean) => void
  onView?: (assetId: string) => void
  onEdit?: (asset: Asset) => void
  onDelete?: (assetId: string) => void
  onScan?: (asset: Asset) => void
  getRiskLevelColor?: (riskLevel: string | undefined | null) => string
  getRiskLevelIcon?: (riskLevel: string | undefined | null) => React.ReactNode
  /** When set, replaces the legacy table with operational intelligence list. */
  intelligenceList?: ReactNode
  /** Enterprise: posture block above filters */
  enterprisePosture?: ReactNode
  /** Enterprise: category/list inside filters panel (after severity chips) */
  enterpriseFiltersExtension?: ReactNode
  /** When true, enterprise UX also shows the filtered inventory table (security-control drills). */
  showFilteredResultsTable?: boolean
  categoryView?: AssetCategoryView | ''
  onCategoryViewChange?: (view: AssetCategoryView | '') => void
}

const AssetsInventoryCard: React.FC<AssetsInventoryCardProps> = ({
  assets,
  visibleAssets,
  sortBy,
  onSortChange,
  severityFilter,
  onSeverityFilterChange,
  typeFilter,
  onTypeFilterChange,
  criticalityFilter,
  onCriticalityFilterChange,
  ownerFilter,
  onOwnerFilterChange,
  environmentFilter,
  onEnvironmentFilterChange,
  searchQuery,
  onSearchChange,
  availableTypes,
  availableCriticalities,
  availableOwners,
  availableEnvironments,
  selectedIds,
  onSelectAsset,
  onSelectAll,
  onView,
  onEdit,
  onDelete,
  onScan,
  getRiskLevelColor,
  getRiskLevelIcon,
  intelligenceList,
  enterprisePosture,
  enterpriseFiltersExtension,
  showFilteredResultsTable = false,
  categoryView = '',
  onCategoryViewChange,
}) => {
  const { t } = useI18n()

  const safeSeverityFilter = severityFilter && severityFilter instanceof Set ? severityFilter : new Set<'critical' | 'high' | 'medium' | 'low'>()
  const safeTypeFilter = typeFilter && typeFilter instanceof Set ? typeFilter : new Set<string>()
  const safeCriticalityFilter = criticalityFilter && criticalityFilter instanceof Set ? criticalityFilter : new Set<string>()
  const safeOwnerFilter = ownerFilter && ownerFilter instanceof Set ? ownerFilter : new Set<string>()
  const safeEnvironmentFilter = environmentFilter && environmentFilter instanceof Set ? environmentFilter : new Set<string>()

  const allSelected = visibleAssets.length > 0 && visibleAssets.every(a => selectedIds.has(a.id))
  const someSelected = visibleAssets.some(a => selectedIds.has(a.id))

  const enterpriseUx = Boolean(intelligenceList && featureFlags.ENTERPRISE_OPERATIONAL_UX_V1)

  const typesForCategory = React.useMemo(
    () => filterTypesForCategoryView(availableTypes, categoryView),
    [availableTypes, categoryView],
  )

  const filtersToolbar = (
    <div className={`${listStyles.panelToolbarFull} ${filterStyles.toolbarFullBleed}`}>
        <div className={listStyles.filtersRow}>
          <div className={listStyles.toolbarField}>
            <Select
              label={t('common.sortBy', 'Sort by')}
              variant={enterpriseUx ? 'outline' : 'default'}
              options={[
                { value: 'critical', label: 'Critical (desc)' },
                { value: 'high', label: 'High (desc)' },
                { value: 'medium', label: 'Medium (desc)' },
                { value: 'total', label: 'Total Vulnerabilities' },
                { value: 'name', label: 'Name (A→Z)' },
              ]}
              value={sortBy}
              onChange={(v) => {
                if (v != null) onSortChange(v as typeof sortBy)
              }}
              placeholder="Sort"
              minWidth="11rem"
            />
          </div>
          <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
            <SearchField
              label={t('assets.inventory.filters.search', 'Search by IP/Hostname/URL')}
              value={searchQuery}
              onChange={onSearchChange}
              placeholder={t('assets.inventory.filters.searchPlaceholder', 'Search...')}
              minWidth="100%"
            />
          </div>
        </div>

        <div className={filterStyles.filterGroups}>
          {enterpriseUx ? (
            <div className={filterStyles.filterGroup}>
              <p className={entStyles.filterGroupLabel}>
                {t('assets.inventory.filters.categoryAndType', 'Category & asset type')}
              </p>
              <div className={filterStyles.chipRow}>
                <button
                  type="button"
                  className={`${entStyles.filterChipToggle} ${!categoryView ? entStyles.filterChipToggleActive : ''}`.trim()}
                  onClick={() => onCategoryViewChange?.('')}
                >
                  {t('assets.categoryViews.all', 'All')}
                </button>
                {ASSET_CATEGORY_VIEWS.map((view) => (
                  <button
                    key={view}
                    type="button"
                    className={`${entStyles.filterChipToggle} ${categoryView === view ? entStyles.filterChipToggleActive : ''}`.trim()}
                    onClick={() => onCategoryViewChange?.(view)}
                  >
                    {t(assetCategoryI18nKey(view), view.replace(/([A-Z])/g, ' $1').trim())}
                  </button>
                ))}
              </div>
              {typesForCategory.length > 0 ? (
                <div className={`${filterStyles.chipRow} ${filterStyles.chipRowNested}`}>
                  {typesForCategory.map((type) => (
                    <label key={type} className={entStyles.filterChipToggle}>
                      <input
                        type="checkbox"
                        checked={safeTypeFilter.has(type)}
                        onChange={(e) => onTypeFilterChange(type, e.target.checked)}
                      />
                      <span>{type || 'UNKNOWN'}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {!enterpriseUx && availableTypes.length > 0 ? (
            <div className={filterStyles.filterGroup}>
              <p className={filterStyles.groupLabel}>{t('assets.inventory.filters.type', 'Type')}</p>
              <div className={filterStyles.chipRow}>
                {availableTypes.map((type) => (
                  <label key={type} className={filterStyles.chipToggle}>
                    <input
                      type="checkbox"
                      checked={safeTypeFilter.has(type)}
                      onChange={(e) => onTypeFilterChange(type, e.target.checked)}
                    />
                    <span>{type || 'UNKNOWN'}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {availableCriticalities.length > 0 && (
            <div className={filterStyles.filterGroup}>
              <p className={enterpriseUx ? entStyles.filterGroupLabel : filterStyles.groupLabel}>
                {t('assets.inventory.filters.criticality', 'Criticality')}
              </p>
              <div className={filterStyles.chipRow}>
                {availableCriticalities.map((crit) => (
                  <label
                    key={crit}
                    className={enterpriseUx ? entStyles.filterChipToggle : filterStyles.chipToggle}
                  >
                    <input
                      type="checkbox"
                      checked={safeCriticalityFilter.has(crit)}
                      onChange={(e) => onCriticalityFilterChange(crit, e.target.checked)}
                    />
                    <span>{crit}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {availableOwners.length > 0 && (
            <div className={filterStyles.filterGroup}>
              <p className={enterpriseUx ? entStyles.filterGroupLabel : filterStyles.groupLabel}>
                {t('assets.inventory.filters.owner', 'Owner')}
              </p>
              <div className={filterStyles.chipRow}>
                {availableOwners.map((owner) => (
                  <label
                    key={owner.id}
                    className={enterpriseUx ? entStyles.filterChipToggle : filterStyles.chipToggle}
                  >
                    <input
                      type="checkbox"
                      checked={safeOwnerFilter.has(owner.id)}
                      onChange={(e) => onOwnerFilterChange(owner.id, e.target.checked)}
                    />
                    <span>{owner.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {availableEnvironments.length > 0 && (
            <div className={filterStyles.filterGroup}>
              <p className={enterpriseUx ? entStyles.filterGroupLabel : filterStyles.groupLabel}>
                {t('assets.inventory.filters.environment', 'Environment')}
              </p>
              <div className={filterStyles.chipRow}>
                {availableEnvironments.map((env) => (
                  <label
                    key={env}
                    className={enterpriseUx ? entStyles.filterChipToggle : filterStyles.chipToggle}
                  >
                    <input
                      type="checkbox"
                      checked={safeEnvironmentFilter.has(env)}
                      onChange={(e) => onEnvironmentFilterChange(env, e.target.checked)}
                    />
                    <span>{env}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className={filterStyles.filterGroup}>
            <p className={enterpriseUx ? entStyles.filterGroupLabel : filterStyles.groupLabel}>
              {t('assets.inventory.filters.severity', 'Severity')}
            </p>
            <div className={filterStyles.chipRow}>
              {(['critical', 'high', 'medium', 'low'] as const).map((sev) => (
                <label
                  key={sev}
                  className={enterpriseUx ? entStyles.filterChipToggle : filterStyles.chipToggle}
                >
                  <input
                    type="checkbox"
                    checked={safeSeverityFilter.has(sev)}
                    onChange={(e) => onSeverityFilterChange(sev, e.target.checked)}
                  />
                  <span>{sev.charAt(0).toUpperCase() + sev.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>

          {enterpriseUx && enterpriseFiltersExtension ? (
            <div className={entStyles.inventoryFiltersExtension}>{enterpriseFiltersExtension}</div>
          ) : null}
        </div>
      </div>
  )

  if (enterpriseUx) {
    return (
      <section
        className={componentScope('AssetsInventoryCard')}
        aria-labelledby="assets-inventory-heading"
      >
        {enterprisePosture}
        <section className={entStyles.layer}>
          <p className={entStyles.layerLabel}>{t('assets.inventory.filtersLayer', 'Search & filters')}</p>
          <div className={entStyles.inventoryFiltersPanel}>{filtersToolbar}</div>
        </section>
        {showFilteredResultsTable ? (
          <section
            className={entStyles.layer}
            id="assets-security-filter-results"
            data-testid="assets-security-filter-results"
            aria-labelledby="assets-security-filter-results-heading"
          >
            <p className={entStyles.layerLabel}>
              {t('assets.securityControls.filteredResults', 'Filtered inventory results')}
            </p>
            <div className={`${listStyles.panelBody} ${filterStyles.inventoryListSection}`}>
              <div className={filterStyles.tableToolbar}>
                <h3 id="assets-security-filter-results-heading" className={filterStyles.tableToolbarTitle}>
                  {t('assets.inventory.tableSection', 'Assets Inventory')} ({visibleAssets.length})
                </h3>
              </div>
              {visibleAssets.length === 0 ? (
                <p className={listStyles.panelMeta}>
                  {t('assets.inventory.noMatches', 'No assets match the current filters.')}
                </p>
              ) : (
                <AssetsInventoryTable
                  assets={visibleAssets}
                  selectedIds={selectedIds}
                  onSelectAsset={onSelectAsset}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onScan={onScan}
                  getRiskLevelColor={getRiskLevelColor!}
                  getRiskLevelIcon={getRiskLevelIcon!}
                />
              )}
            </div>
          </section>
        ) : null}
        {intelligenceList ? (
          <section className={entStyles.layer} aria-labelledby="assets-inventory-heading">
            <p className={entStyles.layerLabel}>{t('assets.inventory.intelligenceLayer', 'Asset inventory')}</p>
            {intelligenceList}
          </section>
        ) : null}
      </section>
    )
  }

  return (
    <section
      className={`${componentScope('AssetsInventoryCard')} ${listStyles.panel}`}
      aria-labelledby="assets-inventory-heading"
    >
      <header className={listStyles.panelHeader}>
        <div className={listStyles.panelIntro}>
          <h2 id="assets-inventory-heading" className={listStyles.panelTitle}>
            {t('assets.inventory.title', 'Assets Inventory')}
          </h2>
          <p className={listStyles.panelMeta}>
            {t('assets.inventory.totalAssets', 'Total Assets')}: <strong>{assets.length}</strong>
            {' · '}
            {t('assets.inventory.visibleAssets', 'Visible')}: <strong>{visibleAssets.length}</strong>
          </p>
        </div>
      </header>

      {filtersToolbar}

      <div className={`${listStyles.panelBody} ${filterStyles.inventoryListSection}`}>
        <>
          <div className={filterStyles.tableToolbar}>
            <h3 className={filterStyles.tableToolbarTitle}>
              {t('assets.inventory.tableSection', 'Assets Inventory')} ({visibleAssets.length})
            </h3>
            <label className={filterStyles.selectAllLabel}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected && !allSelected
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
              <span>{t('assets.inventory.selectAll', 'Select All')}</span>
            </label>
          </div>

          <AssetsInventoryTable
            assets={visibleAssets}
            selectedIds={selectedIds}
            onSelectAsset={onSelectAsset}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onScan={onScan}
            getRiskLevelColor={getRiskLevelColor!}
            getRiskLevelIcon={getRiskLevelIcon!}
          />
        </>
      </div>
    </section>
  )
}

export default AssetsInventoryCard
